import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Package, Plus, Pencil, Trash2, Search, X, Check, ChevronDown, ChevronUp, ExternalLink, Quote, Tag, Users as UsersIcon, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const emptyForm = {
  name: "",
  description: "",
  valueProposition: "",
  differentials: "",
  price: "",
  category: "",
  links: "",
  targetAudienceIds: [] as number[],
};

type FormState = typeof emptyForm;

export function ProductsPanel() {
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const utils = trpc.useUtils();
  const { data: products = [], isLoading } = trpc.aiEmail.listProducts.useQuery();
  const { data: audiences = [] } = trpc.aiEmail.listAudiences.useQuery();

  const createMutation = trpc.aiEmail.createProduct.useMutation({
    onSuccess: () => {
      utils.aiEmail.listProducts.invalidate();
      closeForm();
      toast.success("Produto criado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const updateMutation = trpc.aiEmail.updateProduct.useMutation({
    onSuccess: () => {
      utils.aiEmail.listProducts.invalidate();
      closeForm();
      toast.success("Produto atualizado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteMutation = trpc.aiEmail.deleteProduct.useMutation({
    onSuccess: () => {
      utils.aiEmail.listProducts.invalidate();
      toast.success("Produto removido!");
      setConfirmDeleteId(null);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  function closeForm() {
    setEditingId(null);
    setCreating(false);
    setForm(emptyForm);
  }

  function startCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setCreating(true);
  }

  function startEdit(product: typeof products[number]) {
    const linksObj = product.links as Record<string, string> | null;
    const ids = (product as { targetAudienceIds?: number[] | null }).targetAudienceIds ?? null;
    const fallback = product.targetAudienceId ? [product.targetAudienceId] : [];
    setForm({
      name: product.name,
      description: product.description ?? "",
      valueProposition: product.valueProposition ?? "",
      differentials: (product.differentials as string[] | null)?.join("\n") ?? "",
      price: product.price ?? "",
      category: product.category ?? "",
      links: linksObj ? Object.entries(linksObj).map(([k, v]) => `${k}: ${v}`).join("\n") : "",
      targetAudienceIds: ids && ids.length > 0 ? ids : fallback,
    });
    setEditingId(product.id);
    setCreating(false);
  }

  function toggleAudience(id: number) {
    setForm((prev) => ({
      ...prev,
      targetAudienceIds: prev.targetAudienceIds.includes(id)
        ? prev.targetAudienceIds.filter((x) => x !== id)
        : [...prev.targetAudienceIds, id],
    }));
  }

  function handleSubmit() {
    const parsedDifferentials = form.differentials.split("\n").map((s) => s.trim()).filter(Boolean);
    const parsedLinks: Record<string, string> = {};
    form.links.split("\n").forEach((line) => {
      const [k, ...v] = line.split(":");
      if (k?.trim() && v.length) parsedLinks[k.trim()] = v.join(":").trim();
    });

    const data = {
      name: form.name,
      description: form.description || undefined,
      valueProposition: form.valueProposition || undefined,
      differentials: parsedDifferentials.length ? parsedDifferentials : undefined,
      price: form.price || undefined,
      category: form.category || undefined,
      links: Object.keys(parsedLinks).length ? parsedLinks : undefined,
      targetAudienceIds: form.targetAudienceIds,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  const filtered = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.category ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  const formOpen = creating || editingId !== null;
  const formTitle = editingId ? "Editar Produto" : "Novo Produto";

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Produtos & Ofertas</h2>
          <p className="text-sm text-muted-foreground">
            Cadastre os produtos do negócio para a IA gerar copy alinhado em todos os canais.
          </p>
        </div>
        {!formOpen && (
          <Button onClick={startCreate}>
            <Plus className="w-4 h-4 mr-2" /> Novo Produto
          </Button>
        )}
      </div>

      {formOpen && (
        <Card className="p-5 border-primary/40 bg-primary/[0.02]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm">{formTitle}</h3>
            <Button variant="ghost" size="icon" onClick={closeForm} className="h-7 w-7" aria-label="Fechar">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Curso de Marketing Digital" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descreva o produto/serviço..." rows={3} />
            </div>
            <div>
              <Label>Proposta de Valor</Label>
              <Textarea value={form.valueProposition} onChange={(e) => setForm({ ...form, valueProposition: e.target.value })} placeholder="O que torna este produto único?" rows={2} />
            </div>
            <div>
              <Label>Diferenciais (um por linha)</Label>
              <Textarea value={form.differentials} onChange={(e) => setForm({ ...form, differentials: e.target.value })} placeholder="Suporte 24h&#10;Garantia de 30 dias&#10;Certificado incluso" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Preço</Label>
                <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="R$ 497" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Cursos, SaaS, etc." />
              </div>
            </div>
            <div>
              <Label>Públicos-Alvo Vinculados</Label>
              {audiences.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1">
                  Nenhum público-alvo cadastrado. Vá na aba <strong>Públicos-Alvo</strong> para criar.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {audiences.map((a) => {
                      const selected = form.targetAudienceIds.includes(a.id);
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => toggleAudience(a.id)}
                          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
                            selected
                              ? "bg-primary/10 border-primary/40 text-primary font-medium"
                              : "bg-muted/30 border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40"
                          }`}
                        >
                          {selected && <Check className="w-3 h-3" />}
                          {a.name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Clique para selecionar um ou mais públicos. {form.targetAudienceIds.length} selecionado{form.targetAudienceIds.length !== 1 ? "s" : ""}.
                  </p>
                </>
              )}
            </div>
            <div>
              <Label>Links (um por linha, formato "nome: url")</Label>
              <Textarea value={form.links} onChange={(e) => setForm({ ...form, links: e.target.value })} placeholder="site: https://exemplo.com&#10;vendas: https://exemplo.com/comprar" rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={closeForm}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={!form.name.trim() || createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                )}
                {editingId ? "Salvar" : "Criar"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar produto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">Nenhum produto cadastrado</p>
          <p className="text-sm text-muted-foreground/60">
            Cadastre produtos para que a IA gere copys alinhadas ao seu negócio
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((product) => {
            const isConfirming = confirmDeleteId === product.id;
            const isExpanded = expandedId === product.id;
            const differentials = (product.differentials as string[] | null) ?? [];
            const linksObj = (product.links as Record<string, string> | null) ?? {};
            const linkEntries = Object.entries(linksObj);
            const productAudienceIds = (product as { targetAudienceIds?: number[] | null }).targetAudienceIds
              ?? (product.targetAudienceId ? [product.targetAudienceId] : []);
            const linkedAudiences = audiences.filter((a) => productAudienceIds.includes(a.id));

            return (
              <Card
                key={product.id}
                className={`transition-shadow hover:shadow-md ${isExpanded ? "ring-1 ring-primary/30" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : product.id)}
                  className="w-full text-left p-4 cursor-pointer"
                  aria-expanded={isExpanded}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm leading-tight">{product.name}</h3>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {product.category && (
                          <Badge variant="secondary" className="text-[10px]">
                            <Tag className="w-2.5 h-2.5 mr-1" />
                            {product.category}
                          </Badge>
                        )}
                        {product.price && (
                          <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-200 bg-indigo-50/50">
                            <DollarSign className="w-2.5 h-2.5 mr-0.5" />
                            {product.price}
                          </Badge>
                        )}
                        {linkedAudiences.length > 0 && (
                          <Badge variant="outline" className="text-[10px]">
                            <UsersIcon className="w-2.5 h-2.5 mr-1" />
                            {linkedAudiences.length} público{linkedAudiences.length > 1 ? "s" : ""}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  </div>

                  {product.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>
                  )}
                </button>

                {isExpanded && (
                  <div className="border-t px-4 py-4 space-y-4 bg-muted/10">
                    {product.description && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Descrição</Label>
                        <p className="text-sm mt-1 leading-relaxed">{product.description}</p>
                      </div>
                    )}

                    {product.valueProposition && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Proposta de valor</Label>
                        <div className="mt-1 relative pl-3 border-l-2 border-indigo-200">
                          <Quote className="w-3 h-3 text-indigo-300 absolute -left-[6px] top-0 bg-card" />
                          <p className="text-sm italic text-foreground/80 leading-relaxed">{product.valueProposition}</p>
                        </div>
                      </div>
                    )}

                    {differentials.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Diferenciais</Label>
                        <ul className="mt-1.5 space-y-1">
                          {differentials.map((d, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-sm">
                              <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {linkedAudiences.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Públicos-alvo vinculados</Label>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {linkedAudiences.map((a) => (
                            <Badge key={a.id} variant="secondary" className="text-[10px]">
                              <UsersIcon className="w-2.5 h-2.5 mr-1" />
                              {a.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {linkEntries.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Links</Label>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {linkEntries.map(([k, v]) => (
                            <a
                              key={k}
                              href={v}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" /> {k}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-1">
                      {!isConfirming ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => startEdit(product)} className="h-8">
                            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-destructive hover:text-destructive"
                            onClick={() => setConfirmDeleteId(product.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remover
                          </Button>
                        </>
                      ) : (
                        <div className="flex gap-2 items-center">
                          <span className="text-xs text-muted-foreground">Confirmar remoção?</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={deleteMutation.isPending}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8"
                            onClick={() => deleteMutation.mutate({ id: product.id })}
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            Sim, remover
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
